import React, { Fragment, useId, useEffect, useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty, noop } from 'lodash-es';
import { Row, Col } from 'reactstrap';

// Models
import { VehicleVinEntities } from 'client/data/models/vehicle-vin';

// Utils
import { EventToolbox } from 'client/utils/event-toolbox';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { getSquishVinNoMmyPixelValue } from 'site-modules/shared/utils/appraisal/tracking';
import { fireSelectVinPixel, fireVinDecodeMatchTypePixel } from 'site-modules/shared/utils/appraisal/common-utils';

// Constants
import { TrackingConstant } from 'client/tracking/constant';

// Components
import { FieldError } from 'site-modules/shared/components/field-error/field-error';
import { MultipleVinsModal } from 'site-modules/shared/components/appraisal/multiple-vins-modal/multiple-vins-modal';

// Context
import { useAppraisalTabsContext } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraisal-tabs-context';
import { AppraisalFindVinDrawerContext } from 'site-modules/shared/components/find-vin-modal/appraisal-find-vin-drawer';
import {
  appraisalTabViewPropType,
  getResolvedAppraisalTabViewProps,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';

import { commonEventData } from './license-plate-tab-container';

import './license-plate-tab-content.scss';

export function LicensePlateTab({
  lpInputSlot = null,
  stateCodeInputSlot = null,
  submitButtonSlot = null,
  overlaySubmitButtonSlot = null,
  vinFromLicensePlate = null,
  wasFormSubmitted = false,
  isSubmitting = false,
  vinDecodeResponse = null,
  isLpValid = false,
  setVinDecodeError,
  hideVinLicensePlateBtn = false,
  tabContentModal = null,
  errorMap = new Map(),
  creativeId = '',
  decodedVinError = '',
  componentStyleProperties = {},
  isEmoSearchEnabled = false,
  vehicle = null,
  isAppraisalValuePage = false,
  isLookUpStyleView = false,
  setIsMultipleVinProcessed,
  isMultipleVinProcessed = false,
  onMultipleVinsModalOpen = noop,
  onMultipleVinsModalClose = noop,
  isLicensePlateAndVinStep = false,
  setStylesIds,
  isActiveTab = false,
  view = null,
}) {
  const { mmy, vin, setVin, licensePlate, stateCode, setMmy, setIsReadyForRedirect, isProjectEveStyle, styleIds } =
    useAppraisalTabsContext();
  const { setIsInnerModalOpened, isInnerModalOpened } = useContext(AppraisalFindVinDrawerContext);
  const [isMultipleVinsModalOpen, setIsMultipleVinsModalOpen] = useState(isInnerModalOpened || false);

  const uniqInputId = useId();
  const resolvedView = getResolvedAppraisalTabViewProps({
    view,
    isEmoSearchEnabled,
    isLookUpStyleView,
  });

  const service = vinFromLicensePlate?.service;

  useEffect(() => {
    if (service) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          ...commonEventData,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          subaction_name: TrackingConstant.LICENSE_PLATE_LOOKUP_SERVICE,
          creative_id: creativeId,
          value: service,
        },
      });
    }
  }, [creativeId, service]);

  const vinValueFromLp = vinFromLicensePlate?.vin;

  const vins = vinFromLicensePlate?.vins ?? [];
  const isMultiVins = vins.length > 1;

  useEffect(() => {
    if (!isMultiVins && vinValueFromLp && vin !== vinValueFromLp) {
      setVin(vinValueFromLp);
    }
  }, [isMultiVins, setVin, vin, vinValueFromLp]);

  const hasLicensePlateError = errorMap.has('licensePlate');
  const hasLicensePlateWideError = errorMap.has('licensePlateWide');
  const hasStateCodeError = errorMap.has('stateCode');
  const shouldShowVinError =
    !isSubmitting && !(hasLicensePlateError || hasStateCodeError || hasLicensePlateWideError) && decodedVinError;

  const isVinDecodeResponseEmpty = isEmpty(vinDecodeResponse);
  const vinDecodeStyles = vinDecodeResponse?.results;
  const hasVinDecodeStyles = !!vinDecodeStyles && vinDecodeStyles.length > 0;
  const decoderMatchType = vinDecodeResponse?.matchType;

  useEffect(() => {
    if (isActiveTab && !isVinDecodeResponseEmpty) {
      fireVinDecodeMatchTypePixel(decoderMatchType, creativeId);
    }
    // Cannot use 'isActiveTab' in deps array below, because otherwise the pixel fires on each tab activation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativeId, decoderMatchType, isVinDecodeResponseEmpty]);

  useEffect(() => {
    if (!isMultipleVinProcessed && isMultiVins && isSubmitting && !isMultipleVinsModalOpen) {
      // Intentional modal opening after a multi-VIN lookup completes.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsMultipleVinsModalOpen(true);
      setIsInnerModalOpened(true);
    }
  }, [isMultipleVinProcessed, isMultiVins, isSubmitting, isMultipleVinsModalOpen, setIsInnerModalOpened]);

  const isReadyForUpdate =
    !!vinDecodeStyles && wasFormSubmitted && isLpValid && !isSubmitting && (!isMultiVins || isMultipleVinProcessed);

  const fireAppraiseVehicleTracking = useCallback(
    (subactionName, value) => {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          ...commonEventData,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          creative_id: creativeId,
          subaction_name: subactionName,
          value,
        },
      });
    },
    [creativeId]
  );

  useEffect(() => {
    if (isLpValid && vinValueFromLp) {
      fireSelectVinPixel(vinValueFromLp, creativeId);
    }
  }, [creativeId, isLpValid, vinValueFromLp, wasFormSubmitted]);

  useEffect(() => {
    if (isReadyForUpdate) {
      if (hasVinDecodeStyles) {
        const { makeNiceId, modelNiceId, year } = vinDecodeStyles[0];
        setMmy({ make: makeNiceId, model: modelNiceId, year });
      } else {
        fireAppraiseVehicleTracking(
          TrackingConstant.SUBACTION_VIN_ERROR,
          getSquishVinNoMmyPixelValue(vin, vinDecodeResponse)
        );

        setVinDecodeError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVinDecodeStyles, isReadyForUpdate]);

  const areStyleIdsLoaded = !!styleIds;

  // Make sure all data is set and initiate the redirect
  useEffect(() => {
    if (isReadyForUpdate && !isEmpty(mmy) && areStyleIdsLoaded) {
      delayHeavyTask(() => {
        setIsReadyForRedirect(true);
      });
    }
  }, [isReadyForUpdate, mmy, setIsReadyForRedirect, areStyleIdsLoaded]);

  // Reset multiple VINs modal if LP and/or state code was updated
  useEffect(() => {
    setIsMultipleVinProcessed(false);
  }, [licensePlate, setIsMultipleVinProcessed, stateCode]);

  const {
    FormWrapper,
    licensePlateColSize,
    stateCodeColSize,
    btnWidthMd,
    isVinByAddressOverlay,
    isBackgroundStyleTabEnabled,
    isEscapeHatchDrawer,
    isLocationStyleStep,
    isEscapeHatch,
    isFullWidth,
    isMobile,
    isAddressTab,
  } = componentStyleProperties;

  const closeMultipleVinsModal = useCallback(() => {
    setIsMultipleVinsModalOpen(false);
    setIsInnerModalOpened(false);
  }, [setIsInnerModalOpened]);

  const handleVinFromMultiplesVinsSelect = useCallback(
    async selectedVinData => {
      if (!isEmpty(selectedVinData)) {
        const { vin: vinFromMultipleVinsModal } = selectedVinData;
        setVin(vinFromMultipleVinsModal);
        await setStylesIds(vinFromMultipleVinsModal);
      }
      setIsMultipleVinProcessed(true);
      closeMultipleVinsModal();
    },
    [closeMultipleVinsModal, setIsMultipleVinProcessed, setStylesIds, setVin]
  );

  return (
    <FormWrapper
      isFullWidth={isFullWidth || isLocationStyleStep || isVinByAddressOverlay}
      isEscapeHatch={isEscapeHatch}
    >
      <div
        className={classnames('license-plate-tab', {
          'd-lg-flex align-items-lg-start': (isBackgroundStyleTabEnabled || isProjectEveStyle) && !isAddressTab,
          'w-75': (isBackgroundStyleTabEnabled || isProjectEveStyle) && !isMobile && !isAddressTab,
        })}
      >
        <Row
          className={classnames('appraise-by-license-plate-tab', {
            'mt-1': isEscapeHatchDrawer,
            'flex-grow-1': (isBackgroundStyleTabEnabled || isProjectEveStyle) && !resolvedView.isEmoSearchEnabled,
            'me-lg-0_5': (isBackgroundStyleTabEnabled || isProjectEveStyle) && !isAddressTab,
            'escape-hatch': isEscapeHatch,
          })}
          {...(errorMap.has('licensePlateWide')
            ? { 'aria-describedby': `license-plate-landing-input${uniqInputId}-error`, 'aria-invalid': true }
            : {})}
        >
          <Col {...licensePlateColSize} className={classnames('lp-input-container', { 'px-0': isLocationStyleStep })}>
            {lpInputSlot}
          </Col>
          <Col
            {...stateCodeColSize}
            className={classnames('state-code-input-container', { 'px-0': isLocationStyleStep })}
          >
            {stateCodeInputSlot}
          </Col>
        </Row>
        <div
          className={classnames({
            'mb-2': isVinByAddressOverlay && !isAddressTab,
            ...(isVinByAddressOverlay &&
              !decodedVinError && { 'pt-1': !isLicensePlateAndVinStep, 'pt-0_5': isLicensePlateAndVinStep }),
          })}
        >
          {!isAppraisalValuePage && !isVinByAddressOverlay ? (
            submitButtonSlot
          ) : (
            <Fragment>
              {!isMobile && (
                <Row>
                  <Col xs={12} className={classnames({ 'px-0': isLocationStyleStep })}>
                    <FieldError id="vin-errors" error={shouldShowVinError && decodedVinError} classes="mb-1" />
                    <FieldError
                      id={`license-plate-landing-input${uniqInputId}-error`}
                      error={errorMap.get('licensePlateWide')}
                      classes="mb-1"
                    />
                  </Col>
                  {isLicensePlateAndVinStep && (
                    <Col xs={12} className="size-14 text-cool-gray-50 mb-1">
                      Your license plate will only be used to look up your car. It will not be stored.
                    </Col>
                  )}
                </Row>
              )}
              {(!hideVinLicensePlateBtn || isVinByAddressOverlay) && (
                <Row
                  className={classnames('align-items-center', {
                    'mt-0_5': isEscapeHatch || isLocationStyleStep || isEscapeHatchDrawer,
                  })}
                >
                  {isLicensePlateAndVinStep && isMobile && (
                    <Col xs={12} className="size-14 text-cool-gray-50 mb-1">
                      Your license plate will only be used to look up your car. It will not be stored.
                    </Col>
                  )}
                  <Col
                    xs={12}
                    md={isEscapeHatch || isVinByAddressOverlay ? 12 : btnWidthMd}
                    className={classnames({
                      'px-0': isLocationStyleStep,
                      'mt-1': isLicensePlateAndVinStep && isAddressTab && isVinByAddressOverlay,
                    })}
                  >
                    {overlaySubmitButtonSlot}
                    {isMobile && (
                      <Fragment>
                        <FieldError id="vin-errors" error={shouldShowVinError && decodedVinError} classes="mt-0_5" />
                        <FieldError
                          id={`license-plate-landing-input${uniqInputId}-error`}
                          error={errorMap.get('licensePlateWide')}
                          classes="mt-0_5"
                        />
                      </Fragment>
                    )}
                  </Col>
                  {tabContentModal && (
                    <Col
                      xs={12}
                      md={isEscapeHatch ? 12 : 6}
                      className={classnames('mt-1 text-center', {
                        'text-md-start mt-md-0': !isEscapeHatch,
                      })}
                    >
                      {tabContentModal}
                    </Col>
                  )}
                </Row>
              )}
            </Fragment>
          )}
        </div>
      </div>
      {!isAppraisalValuePage && !isVinByAddressOverlay && (
        <FieldError
          id={`license-plate-landing-input${uniqInputId}-error`}
          error={errorMap.get('licensePlateWide')}
          classes={classnames('mt-1', {
            'license-plate-tab-content-verbose-error mt-lg-0': isBackgroundStyleTabEnabled,
          })}
        />
      )}
      <MultipleVinsModal
        isOpen={isMultipleVinsModalOpen}
        toggle={closeMultipleVinsModal}
        vins={vins}
        onSelectVin={handleVinFromMultiplesVinsSelect}
        vehicle={vehicle}
        isAppraisalValuePage={isAppraisalValuePage}
        isLookUpStyleView={resolvedView.isLookUpStyleView}
        onOpen={onMultipleVinsModalOpen}
        onClose={onMultipleVinsModalClose}
        parentCreativeId={creativeId}
      />
    </FormWrapper>
  );
}

LicensePlateTab.propTypes = {
  lpInputSlot: PropTypes.node,
  stateCodeInputSlot: PropTypes.node,
  submitButtonSlot: PropTypes.node,
  overlaySubmitButtonSlot: PropTypes.node,
  vinFromLicensePlate: PropTypes.shape({}),
  wasFormSubmitted: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  vinDecodeResponse: PropTypes.shape({
    results: VehicleVinEntities.VinStylesBasicInfo,
    status: PropTypes.number,
    hasError: PropTypes.bool,
  }),
  isLpValid: PropTypes.bool,
  setVinDecodeError: PropTypes.func.isRequired,
  hideVinLicensePlateBtn: PropTypes.bool,
  tabContentModal: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  errorMap: PropTypes.shape({}),
  creativeId: PropTypes.string,
  decodedVinError: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]),
  componentStyleProperties: PropTypes.shape({}),
  isEmoSearchEnabled: PropTypes.bool,
  vehicle: PropTypes.shape({
    make: PropTypes.shape({
      slug: PropTypes.string,
    }),
    model: PropTypes.shape({
      slug: PropTypes.string,
    }),
    year: PropTypes.shape({
      year: PropTypes.string,
    }),
  }),
  isAppraisalValuePage: PropTypes.bool,
  isLookUpStyleView: PropTypes.bool,
  setIsMultipleVinProcessed: PropTypes.func.isRequired,
  onMultipleVinsModalOpen: PropTypes.func,
  onMultipleVinsModalClose: PropTypes.func,
  isMultipleVinProcessed: PropTypes.bool,
  isLicensePlateAndVinStep: PropTypes.bool,
  setStylesIds: PropTypes.func.isRequired,
  isActiveTab: PropTypes.bool,
  view: appraisalTabViewPropType,
};
